import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Button, Alert, Card, OtpInput, Badge, Logo, ORIO360_TAGLINE } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { authService } from '@/services/auth.service';
import { ApiError } from '@/lib/api-client';
import type { TotpSetupResponse } from '@/types/auth';
import QRCode from 'qrcode';

type AuthMode =
  | 'otp-request'
  | 'otp-verify'
  | 'totp-verify'
  | 'totp-setup-otp'
  | 'totp-setup-qr'
  | 'totp-setup-confirm';

const DEFAULT_REDIRECT = '/pos/billing';

// Pre-auth marketing copy — no tenant context here, so the feature card stays
// region-neutral instead of asserting India-only vocabulary (GST/CGST/GSTR1).
// Tenant-specific tax-regime wording surfaces once the user is signed in.
const FEATURES = [
  { title: 'Offline-First Billing', description: 'Process sales even without internet connectivity' },
  { title: 'Tax Compliant', description: 'Automatic tax breakdown and statutory exports built in' },
  { title: 'Multi-Location', description: 'Manage inventory and billing across all your stores' },
  { title: 'Real-Time Sync', description: 'Instant updates across devices and locations' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, verifyOtp, verifyTotp } = useAuthStore();

  const [mode, setMode] = useState<AuthMode>('otp-request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [otpSentMessage, setOtpSentMessage] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // TOTP setup state
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [setupCode, setSetupCode] = useState('');

  const redirectTo = searchParams.get('redirect') || DEFAULT_REDIRECT;
  const sessionExpired = searchParams.get('reason') === 'session-expired';

  useEffect(() => {
    if (!sessionExpired) return;
    // Arrived here because the api-client cleared the session. The test's
    // addInitScript / a stale localStorage entry can otherwise re-hydrate
    // isAuthenticated=true and bounce the user back to /pos/billing.
    // Mount-only: re-running on isAuthenticated would undo the login the
    // user is about to make from this page (the URL keeps the reason param).
    try { localStorage.removeItem('bbpos_user'); } catch { /* ignore */ }
    useAuthStore.setState({ user: null, profile: null, isAuthenticated: false });
  }, []);

  useEffect(() => {
    // After a session-expired redirect the login handlers navigate themselves.
    if (isAuthenticated && !sessionExpired) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo, sessionExpired]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Generate QR data URL when setup data arrives
  useEffect(() => {
    if (setupData?.qr_uri) {
      QRCode.toDataURL(setupData.qr_uri, { width: 200, margin: 2 }).then(setQrDataUrl).catch(() => {});
    }
  }, [setupData?.qr_uri]);

  const clearErrors = useCallback(() => {
    setApiError('');
    setOtpSentMessage('');
  }, []);

  const goBack = useCallback(() => {
    clearErrors();
    setOtp('');
    setTotpCode('');
    setRecoveryCode('');
    setShowRecovery(false);
    setSetupData(null);
    setSetupCode('');
    setMode('otp-request');
  }, [clearErrors]);

  // ── Email submit: check login method, then route to correct mode ──────

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      const { method } = await authService.getLoginMethod(email.trim());

      if (method === 'totp') {
        setMode('totp-verify');
      } else if (method === 'totp_setup') {
        // Need identity verification first, then QR setup
        const result = await authService.requestOtp(email.trim());
        setOtpSentMessage(result.message);
        setMode('totp-setup-otp');
        setResendCooldown(60);
      } else {
        // Standard email OTP flow
        const result = await authService.requestOtp(email.trim());
        setOtpSentMessage(result.message);
        setMode('otp-verify');
        setResendCooldown(60);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Failed to proceed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Standard OTP verify ───────────────────────────────────────────────

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || otp.length !== 6) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      await verifyOtp(email.trim(), otp);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message.toLowerCase().includes('expired')) {
          setApiError('OTP expired — resend?');
          setResendCooldown(0);
        } else {
          setApiError(err.message);
        }
      } else {
        setApiError('OTP verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── TOTP verify ───────────────────────────────────────────────────────

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();

    clearErrors();
    setIsSubmitting(true);
    try {
      if (showRecovery) {
        if (!recoveryCode.trim()) return;
        const data = await authService.verifyTotpRecovery(email.trim(), recoveryCode.trim());
        localStorage.setItem('bbpos_user', JSON.stringify(data.user));
        useAuthStore.setState({ user: data.user, isAuthenticated: true });
        await useAuthStore.getState().fetchProfile();
        navigate(redirectTo, { replace: true });
      } else {
        if (totpCode.length !== 6) return;
        await verifyTotp(email.trim(), totpCode);
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Verification failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── TOTP setup: verify identity via email OTP, then get QR ────────────

  const handleSetupOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || otp.length !== 6) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      const data = await authService.initTotpSetup(email.trim(), otp);
      setSetupData(data);
      setMode('totp-setup-qr');
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Setup failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── TOTP setup: confirm with authenticator code ───────────────────────

  const handleSetupConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!setupData?.setup_session || setupCode.length !== 6) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      const data = await authService.confirmTotpSetupPublic(setupData.setup_session, setupCode);
      localStorage.setItem('bbpos_user', JSON.stringify(data.user));
      useAuthStore.setState({ user: data.user, isAuthenticated: true });
      await useAuthStore.getState().fetchProfile();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Confirmation failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    clearErrors();
    try {
      const result = await authService.requestOtp(email.trim());
      setOtpSentMessage(result.message);
      setResendCooldown(60);
    } catch (err) {
      if (err instanceof ApiError) {
        setApiError(err.message);
      } else {
        setApiError('Failed to resend OTP.');
      }
    }
  };

  // ── Mode-specific heading ─────────────────────────────────────────────

  const heading = (() => {
    switch (mode) {
      case 'otp-verify': return 'Verify OTP';
      case 'totp-verify': return 'Authenticator Code';
      case 'totp-setup-otp': return 'Verify Your Identity';
      case 'totp-setup-qr': return 'Set Up Authenticator';
      case 'totp-setup-confirm': return 'Confirm Setup';
      default: return 'Welcome back';
    }
  })();

  const subheading = (() => {
    switch (mode) {
      case 'otp-verify': return 'Enter the code we sent to your email';
      case 'totp-verify': return 'Enter the 6-digit code from your authenticator app';
      case 'totp-setup-otp': return 'Enter the email OTP to verify your identity before setting up authenticator';
      case 'totp-setup-qr': return 'Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)';
      case 'totp-setup-confirm': return 'Enter the 6-digit code from your authenticator app to complete setup';
      default: return 'Sign in to your POS terminal';
    }
  })();

  return (
    <div className="flex min-h-screen">
      {/* Left Panel — Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
          <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="relative z-10 flex flex-col justify-between w-full p-12">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Logo variant="lockup" tone="white" size={36} />
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-extrabold text-white leading-tight">
                {ORIO360_TAGLINE}
              </h1>
              <p className="mt-4 text-lg text-brand-100 max-w-md">
                The all-in-one point of sale system built for retail and restaurant businesses.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {FEATURES.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-xl bg-white/10 backdrop-blur-sm p-4 border border-white/10"
                >
                  <h3 className="text-sm font-semibold text-white mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-brand-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-brand-300">
            &copy; {new Date().getFullYear()} Orio360. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex flex-1 items-center justify-center bg-neutral-50 dark:bg-neutral-900 px-6 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile-only branding */}
          <div className="lg:hidden text-center">
            <Logo variant="mark" size={48} className="inline-block mb-3" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{heading}</h2>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{subheading}</p>
          </div>

          {sessionExpired && (
            <Alert
              variant="warning"
              title="Session Expired"
              description="Your session has expired. Please sign in again to continue."
            />
          )}

          {apiError && (
            <Alert
              variant="danger"
              title="Login Failed"
              description={apiError}
              dismissible
              onDismiss={() => setApiError('')}
            />
          )}

          {otpSentMessage && !apiError && (
            <Alert
              variant="info"
              description={otpSentMessage}
              dismissible
              onDismiss={() => setOtpSentMessage('')}
            />
          )}

          <Card variant="outlined" padding="lg">
            {/* ═══ Email Entry (checks login method) ═══ */}
            {mode === 'otp-request' && (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="you@business.com"
                  value={email}
                  onChange={(v) => { setEmail(v); if (apiError) setApiError(''); }}
                  disabled={isSubmitting}
                  required
                  name="email"
                  size="md"
                />
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || !email.trim()}>
                  Continue
                </Button>
              </form>
            )}

            {/* ═══ Standard Email OTP Verify ═══ */}
            {mode === 'otp-verify' && (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Code sent to <Badge variant="info" size="sm">{email}</Badge>
                  </p>
                </div>
                <div className="flex justify-center py-2">
                  <OtpInput length={6} value={otp} onChange={(v) => { setOtp(v); if (apiError) setApiError(''); }} disabled={isSubmitting} autoFocus onComplete={setOtp} />
                </div>
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || otp.length !== 6}>
                  Verify & Sign In
                </Button>
                <div className="flex items-center justify-between pt-2">
                  <Button variant="link" size="sm" onClick={goBack} disabled={isSubmitting}>Back to login</Button>
                  <Button variant="ghost" size="sm" onClick={handleResendOtp} disabled={resendCooldown > 0 || isSubmitting}>
                    {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend OTP'}
                  </Button>
                </div>
              </form>
            )}

            {/* ═══ TOTP Verify (authenticator app) ═══ */}
            {mode === 'totp-verify' && (
              <form onSubmit={handleVerifyTotp} className="space-y-5">
                {!showRecovery ? (
                  <>
                    <div className="flex justify-center py-2">
                      <OtpInput length={6} value={totpCode} onChange={(v) => { setTotpCode(v); if (apiError) setApiError(''); }} disabled={isSubmitting} autoFocus onComplete={setTotpCode} />
                    </div>
                    <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || totpCode.length !== 6}>
                      Verify & Sign In
                    </Button>
                  </>
                ) : (
                  <>
                    <Input
                      label="Recovery Code"
                      placeholder="AB12CD34"
                      value={recoveryCode}
                      onChange={(v) => { setRecoveryCode(v.toUpperCase()); if (apiError) setApiError(''); }}
                      disabled={isSubmitting}
                      name="recovery-code"
                      size="md"
                    />
                    <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || recoveryCode.length !== 8}>
                      Verify & Sign In
                    </Button>
                  </>
                )}
                <div className="flex items-center justify-between pt-2">
                  <Button variant="link" size="sm" onClick={goBack} disabled={isSubmitting}>Back to login</Button>
                  <Button variant="ghost" size="sm" onClick={() => { setShowRecovery(!showRecovery); clearErrors(); }} disabled={isSubmitting}>
                    {showRecovery ? 'Use authenticator' : 'Use recovery code'}
                  </Button>
                </div>
              </form>
            )}

            {/* ═══ TOTP Setup: Identity verification via email OTP ═══ */}
            {mode === 'totp-setup-otp' && (
              <form onSubmit={handleSetupOtpVerify} className="space-y-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Code sent to <Badge variant="info" size="sm">{email}</Badge>
                  </p>
                  <p className="text-xs text-neutral-400 mt-1">Verify your identity to set up authenticator</p>
                </div>
                <div className="flex justify-center py-2">
                  <OtpInput length={6} value={otp} onChange={(v) => { setOtp(v); if (apiError) setApiError(''); }} disabled={isSubmitting} autoFocus onComplete={setOtp} />
                </div>
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || otp.length !== 6}>
                  Verify & Continue Setup
                </Button>
                <div className="flex items-center justify-between pt-2">
                  <Button variant="link" size="sm" onClick={goBack} disabled={isSubmitting}>Back to login</Button>
                  <Button variant="ghost" size="sm" onClick={handleResendOtp} disabled={resendCooldown > 0 || isSubmitting}>
                    {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend OTP'}
                  </Button>
                </div>
              </form>
            )}

            {/* ═══ TOTP Setup: QR Code + Recovery Codes ═══ */}
            {mode === 'totp-setup-qr' && setupData && (
              <div className="space-y-5">
                <div className="flex justify-center">
                  {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR Code" className="w-48 h-48" />}
                </div>
                <div className="text-center">
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Or enter manually:</p>
                  <code className="text-xs bg-neutral-100 dark:bg-neutral-700 px-3 py-1.5 rounded font-mono select-all">
                    {setupData.secret}
                  </code>
                </div>
                <div className="bg-warning-50 dark:bg-warning-900/20 rounded-lg p-4">
                  <p className="text-sm font-semibold text-warning-700 dark:text-warning-400 mb-2">
                    Save your recovery codes
                  </p>
                  <p className="text-xs text-warning-600 dark:text-warning-300 mb-3">
                    Store these in a safe place. Each code can only be used once.
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {setupData.recovery_codes.map((code) => (
                      <code key={code} className="text-xs font-mono bg-white dark:bg-neutral-800 px-2 py-1 rounded text-center select-all">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
                <Button variant="primary" size="lg" fullWidth onClick={() => setMode('totp-setup-confirm')}>
                  I've saved my codes — Continue
                </Button>
              </div>
            )}

            {/* ═══ TOTP Setup: Confirm with authenticator code ═══ */}
            {mode === 'totp-setup-confirm' && (
              <form onSubmit={handleSetupConfirm} className="space-y-5">
                <div className="text-center">
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    Enter the 6-digit code from your authenticator app to complete setup
                  </p>
                </div>
                <div className="flex justify-center py-2">
                  <OtpInput length={6} value={setupCode} onChange={(v) => { setSetupCode(v); if (apiError) setApiError(''); }} disabled={isSubmitting} autoFocus onComplete={setSetupCode} />
                </div>
                <Button type="submit" variant="primary" size="lg" fullWidth loading={isSubmitting} disabled={isSubmitting || setupCode.length !== 6}>
                  Confirm & Sign In
                </Button>
                <div className="pt-2">
                  <Button variant="link" size="sm" onClick={() => setMode('totp-setup-qr')} disabled={isSubmitting}>
                    Back to QR code
                  </Button>
                </div>
              </form>
            )}
          </Card>

          <p className="text-center text-xs text-neutral-400 dark:text-neutral-500 lg:hidden">
            &copy; {new Date().getFullYear()} Orio360. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
